import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { PaymentAdditions } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/depositSignupPage/PaymentsAdditions';
import { Input } from 'src/domains/players/webview/components/form/input/Input';
import { RingFencedFundsWrapper } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/depositSignupPage/RingFencedFundsWrapper';
import { PageSectionWrapper } from 'src/domains/players/webview/components/SignUp';
import {
    DepositHeader,
    PayButton,
    CardInfo,
} from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/depositSignupPage/Deposit.style';
import { RealexTabState } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/realexProvider/RealexTab.state';
import { KycWarning } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/kycWarning/KycWarning';
import { mapErrorsMessages } from 'src/domains/players/webview/components/WithdrawAndDeposit/depositProcedure/topUpProcedureParts/errorMessages/DepositErrorMessages';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';
import { YaspaSegment } from 'src/domains/players/webview/components/WithdrawAndDeposit/yaspa/YaspaSegment/YaspaSegment';
import { BasicDataModel } from 'src/domains/players/state/BasicDataModel';
import { EnvironmentState } from 'src/domains/layouts/state/environmentState/EnvironmentState';
import { isMobileVersionEqualOrHigher } from 'src/utils/mobileApp';
import { useCommon } from 'src/domains/common/Common';

interface DepositPropsType {
    state: RealexTabState;
}

export const DepositSignupRealex = observer('DepositSignupRealex', ({ state }: DepositPropsType) => {
    const common = useCommon();
    const { paymentProviders } = ConfigComponents.get(common).config;
    const configComponents = ConfigComponents.get(common);
    const languagesState = LanguagesState.get(common);
    const basicDataModel = BasicDataModel.get(common);
    const envState = EnvironmentState.get(common);
    const isMobileApp = envState.isMobileApp();
    const mobileAppVersion = envState.mobileAppVersion;
    const shouldDisplayYaspaForMobileApp = isMobileApp
        ? isMobileVersionEqualOrHigher(mobileAppVersion, configComponents.config.yaspaMobileAppValidVersion)
        : true;

    return (
        <PageSectionWrapper>
            <DepositHeader data-test='deposit-header'>
                <I18n
                    langKey='signup-journey.deposit.title'
                    defaultText='Deposit'
                />
            </DepositHeader>
            <Input
                type='text'
                maxLength={10}
                placeholder='0.00'
                currency={basicDataModel.moneySymbol}
                label={
                    <I18n
                        langKey='signup-journey.deposit-input.label'
                        defaultText='Deposit amount'
                    />
                }
                state={state.depositAmount}
                inputRef={state.setDepositInputRef}
                onChange={state.onChange}
                onBlur={state.setAmount}
                colorTheme='dark'
                dataTest='deposit-amount-input'
            />
            <PaymentAdditions
                key='payment-additions'
                currency={basicDataModel.moneySymbol}
                value={state.depositAmount.value}
                onChange={state.handleAdditionsChange}
            />

            {state.errorMessagesState.errors.length > 0
                ? state.errorMessagesState.errors.map((error) => mapErrorsMessages(error, languagesState))
                : null}
            {state.hasRingFencedFunds === false ? (
                <RingFencedFundsWrapper
                    isMoreShown={state.showMoreRingFenceFlag}
                    toggleShowMore={state.toggleShowMore}
                    isSignup={true}
                />
            ) : null}
            <PayButton
                size='large'
                disabled={state.isButtonDisabled}
                onClick={state.submitDepositForm}
                dataTest='deposit-pay-debit-card'
            >
                <I18n
                    langKey='signup-journey.deposit.pay-card-button'
                    defaultText='Pay using debit card'
                />
            </PayButton>
            <CardInfo data-test='deposit-card-info'>
                <I18n
                    langKey='signup-journey.deposit.card-info'
                    defaultText='Credit cards are no longer accepted'
                />
            </CardInfo>
            {paymentProviders.openBanking?.type === 'yaspa' &&
            paymentProviders.openBanking.allowOnlyWithdraw === false &&
            shouldDisplayYaspaForMobileApp ? (
                <YaspaSegment
                    viewType='deposit'
                    depositYaspaPage={state.depositYaspaPage}
                    isSignup={true}
                />
            ) : null}
            {configComponents.config.showKycStatus === true ? <KycWarning isSignup={true} /> : null}
        </PageSectionWrapper>
    );
});
